/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * shareActions.js
 *
 * The side-effecting half of "Share to Happy": every function here takes the
 * files the user shared (already filtered by `sharePayload.partitionItemsForDestination`)
 * and delivers them to ONE destination through the feature's existing,
 * production upload path — nothing new is invented server-side:
 *
 *  - Team Chat   → `uploadChatAttachment` per file, then ONE `sendChatMessage`;
 *  - work order  → `uploadWorkOrderPhoto` (photos, resized, `photo:<category>`)
 *                  or `uploadWorkOrderDocument` (PDF / Office / text);
 *  - lead, customer, project, task → generic `POST /api/attachments/{type}/{id}`.
 *
 * Uploads run one by one (a phone on mobile data should not push ten files in
 * parallel) and never stop at the first failure: the result lists what got
 * through and what did not, so the screen can tell the user exactly that.
 *
 * Also hosts the small, fail-soft list readers the destination pickers use, and
 * {@link discardShareFiles}, which removes the temporary copies the OS handed
 * over (Android cache dir / iOS App Group container — the latter is never purged
 * by the system) once a share was delivered or dropped.
 *
 * @module helpers/share/shareActions
 */

import {Platform} from 'react-native';
import * as FileSystem from 'expo-file-system';
import {sendChatMessage, uploadChatAttachment} from '../../services/teamChatApi';
import {uploadWorkOrderDocument, uploadWorkOrderPhoto} from '../../services/workOrderPhotoApi';
import {uploadEntityAttachment} from '../../services/entityDocumentsApi';
import {discardPreparedPhoto, preparePhotoForUpload} from '../preparePhotoForUpload';
import {makeClientMessageId} from '../teamChat/chatShapes';
import {apiFetch} from '../WorkSheetContext';
import {fetchManagementUnifiedCustomers} from '../managementApi';
import {SHARE_KINDS} from './sharePayload';

/** Error code thrown when a chat share ends up with neither a file nor text. */
export const SHARE_NOTHING_TO_SEND = 'SHARE_NOTHING_TO_SEND';

/**
 * Delete the local copies of shared files. Only `file://` URIs are touched
 * (they are the share's own copies); failures are ignored — a leftover temp file
 * must never break the flow.
 *
 * @param {Array<{uri?: string}>} items Shared files.
 * @returns {Promise<void>}
 */
export const discardShareFiles = async (items) => {
    if (Platform.OS === 'web' || typeof FileSystem.deleteAsync !== 'function') {
        return;
    }

    const uris = (Array.isArray(items) ? items : [])
        .map((item) => String(item?.uri || ''))
        .filter((uri) => uri.startsWith('file://'));

    await Promise.all(uris.map((uri) => FileSystem.deleteAsync(uri, {idempotent: true}).catch(() => {})));
};

/**
 * @typedef {Object} ShareDeliveryResult
 * @property {import('./sharePayload').ShareItem[]} succeeded Files that were stored.
 * @property {Array<{item: import('./sharePayload').ShareItem, error: Error}>} failed Files that were not.
 * @property {*} [message] The created chat message (Team Chat only).
 */

/**
 * Run `upload` for every item sequentially, collecting successes and failures.
 *
 * @param {import('./sharePayload').ShareItem[]} items Files to deliver.
 * @param {(item: object) => Promise<*>} upload Uploader for one file.
 * @param {(progress: {done: number, total: number, item: object}) => void} [onProgress] Progress callback.
 * @returns {Promise<{succeeded: object[], failed: object[], results: Array<*>}>}
 */
const uploadSequentially = async (items, upload, onProgress) => {
    const list = Array.isArray(items) ? items : [];
    const succeeded = [];
    const failed = [];
    const results = [];

    for (let index = 0; index < list.length; index += 1) {
        const item = list[index];

        onProgress?.({done: index, total: list.length, item});
        try {
            results.push(await upload(item));
            succeeded.push(item);
        } catch (error) {
            failed.push({item, error});
        }
    }
    onProgress?.({done: list.length, total: list.length, item: null});

    return {succeeded, failed, results};
};

/**
 * The asset descriptor the existing uploaders expect.
 * @param {import('./sharePayload').ShareItem} item Shared file.
 * @returns {{uri: string, name: string, fileName: string, mimeType: string, size: number}}
 */
const toAsset = (item) => ({
    uri: item.uri,
    name: item.name,
    fileName: item.name,
    mimeType: item.mimeType,
    size: item.size,
});

/**
 * Post the share into a Team Chat conversation: every file is uploaded as an
 * attachment (photos resized first, like the composer does), then ONE message
 * carries the text and all attachments that made it.
 *
 * @param {Object} params
 * @param {string} params.channelId Target conversation.
 * @param {import('./sharePayload').ShareItem[]} params.items Accepted files.
 * @param {string} params.text Message text (note + shared text / link).
 * @param {Function} [params.onProgress] Progress callback.
 * @returns {Promise<ShareDeliveryResult>}
 * @throws {Error} When nothing could be sent at all (every upload failed and there is no text, or the send failed).
 */
export const sendShareToChat = async ({channelId, items, text, onProgress}) => {
    const {succeeded, failed, results} = await uploadSequentially(items, async (item) => {
        const asset = toAsset(item);

        if (item.kind !== SHARE_KINDS.IMAGE) {
            return uploadChatAttachment(channelId, asset);
        }

        const prepared = await preparePhotoForUpload(asset);

        try {
            return await uploadChatAttachment(channelId, {
                ...prepared,
                name: prepared !== asset ? item.name.replace(/\.(heic|heif|png|webp)$/i, '.jpg') : item.name,
                mimeType: prepared?.mimeType || item.mimeType,
            });
        } finally {
            if (prepared && prepared !== asset) {
                discardPreparedPhoto(prepared);
            }
        }
    }, onProgress);

    const body = String(text || '').trim();

    if (!results.length && !body) {
        if (failed[0]?.error) {
            throw failed[0].error;
        }

        const error = new Error('Nothing to send');

        error.code = SHARE_NOTHING_TO_SEND;
        throw error;
    }

    const message = await sendChatMessage(channelId, {
        text: body,
        clientMessageId: makeClientMessageId(),
        attachments: results.filter(Boolean),
        recordRefs: [],
        mentions: {users: [], channel: false},
    });

    return {succeeded, failed, message};
};

/**
 * Attach the shared files to a work order: photos land in its photo gallery
 * under `category`, every other accepted file as a work-order document.
 *
 * @param {Object} params
 * @param {string} params.workOrderId Server id of the work order.
 * @param {import('./sharePayload').ShareItem[]} params.items Accepted files.
 * @param {string} [params.category] Photo category (`serviceMode.PHOTO_CATEGORIES` key).
 * @param {string} [params.restaurantId] Work order's store.
 * @param {Function} [params.onProgress] Progress callback.
 * @returns {Promise<ShareDeliveryResult>}
 */
export const attachShareToWorkOrder = async ({workOrderId, items, category, restaurantId, onProgress}) => {
    const {succeeded, failed} = await uploadSequentially(items, (item) => (item.kind === SHARE_KINDS.IMAGE
        ? uploadWorkOrderPhoto(workOrderId, toAsset(item), category, {restaurantId})
        : uploadWorkOrderDocument(workOrderId, toAsset(item), {restaurantId})), onProgress);

    return {succeeded, failed};
};

/**
 * Attach the shared files to a CRM / project record through the generic
 * attachments API (`lead`, `customer`, `project`, `task`).
 *
 * @param {Object} params
 * @param {string} params.entityType Attachments-registry entity type.
 * @param {string} params.entityId Record id.
 * @param {import('./sharePayload').ShareItem[]} params.items Accepted files.
 * @param {string} [params.restaurantId] Active store.
 * @param {Function} [params.onProgress] Progress callback.
 * @returns {Promise<ShareDeliveryResult>}
 */
export const attachShareToEntity = async ({entityType, entityId, items, restaurantId, onProgress}) => {
    const {succeeded, failed} = await uploadSequentially(
        items,
        (item) => uploadEntityAttachment(entityType, entityId, toAsset(item), {restaurantId}),
        onProgress,
    );

    return {succeeded, failed};
};

/* ───────────────────────────── picker readers ───────────────────────────── */

const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

const idOfRecord = (record) => String(record?._id || record?.id || '');

const listOf = (response, keys) => {
    if (Array.isArray(response)) {
        return response;
    }
    for (const key of keys) {
        if (Array.isArray(response?.[key])) {
            return response[key];
        }
        if (Array.isArray(response?.data?.[key])) {
            return response.data[key];
        }
    }

    return Array.isArray(response?.data) ? response.data : [];
};

/**
 * @typedef {Object} SharePickerRow
 * @property {string} id Record id.
 * @property {string} title Primary line.
 * @property {string} [subtitle] Secondary line.
 * @property {string} [icon] MDI glyph.
 */

/**
 * Search CRM leads for the lead picker (`GET /crm/leads?search=`).
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} term Search term ('' = newest).
 * @returns {Promise<SharePickerRow[]>}
 */
export const searchShareLeads = async (scope, term) => {
    const query = [
        'page=1',
        'limit=40',
        'sortBy=updatedAt',
        'sortOrder=desc',
        term ? `search=${encodeURIComponent(term)}` : '',
    ].filter(Boolean).join('&');
    const leads = listOf(await apiFetch(`/crm/leads?${query}`, {headers: scopeHeaders(scope)}), ['leads', 'items']);

    return leads.map((lead) => {
        const company = lead?.companyName || lead?.company?.name || (typeof lead?.company === 'string' ? lead.company : '');
        const name = lead?.name || [lead?.firstName, lead?.lastName].filter(Boolean).join(' ');

        return {
            id: idOfRecord(lead),
            title: String(name || company || lead?.email || '').trim(),
            subtitle: [name ? company : '', lead?.email, lead?.phone].filter(Boolean).join(' · '),
            icon: 'account-search-outline',
        };
    }).filter((row) => row.id && row.title);
};

/**
 * Search the store's own customers for the customer picker. Only the
 * `restaurant` source is attachable through the attachments registry, so the
 * unified index is asked for that bucket alone.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} term Search term ('' = first page).
 * @returns {Promise<SharePickerRow[]>}
 */
export const searchShareCustomers = async (scope, term) => {
    const customers = await fetchManagementUnifiedCustomers(scope, {
        search: term || undefined,
        types: 'restaurant',
        includeRelatedStats: false,
        limit: 40,
    });

    return (Array.isArray(customers) ? customers : [])
        .filter((customer) => !customer?.source || customer.source === 'restaurant')
        .map((customer) => {
            const name = customer?.name || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ');

            return {
                id: idOfRecord(customer),
                title: String(name || customer?.companyName || customer?.email || '').trim(),
                subtitle: [customer?.companyName, customer?.email, customer?.phone].filter(Boolean).join(' · '),
                icon: 'account-tie-outline',
            };
        })
        .filter((row) => row.id && row.title);
};
